import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ClipboardCheck, FileCheck2, Wrench, KeyRound, CheckCircle2, PhoneCall, CircleCheck } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { PlateChip } from '../components/PlateChip';
import { api, fileUrl } from '../lib/api';
import { manilaDateTime } from '../lib/format';

// Public tracking page — no login. Status + sanitized timeline only:
// no prices, no photos, no staff names. The shop's branding front and center.

const STATUS_UI = {
  received: { icon: ClipboardCheck, bg: 'bg-[#E6F1FB]', fg: 'text-[#0C447C]', pill: 'bg-slate-100 text-slate-600' },
  estimate_sent: { icon: FileCheck2, bg: 'bg-[#E6F1FB]', fg: 'text-[#0C447C]', pill: 'bg-sky-100 text-sky-800' },
  approved: { icon: FileCheck2, bg: 'bg-[#E6F1FB]', fg: 'text-[#0C447C]', pill: 'bg-sky-100 text-sky-800' },
  in_progress: { icon: Wrench, bg: 'bg-[#FAECE7]', fg: 'text-[#712B13]', pill: 'bg-orange-100 text-orange-800' },
  ready: { icon: KeyRound, bg: 'bg-[#EAF3DE]', fg: 'text-[#27500A]', pill: 'bg-green-100 text-green-800' },
  closed: { icon: CircleCheck, bg: 'bg-slate-100', fg: 'text-slate-700', pill: 'bg-slate-200 text-slate-700' },
};

const PILL_LABEL = {
  received: 'Received', estimate_sent: 'Estimate sent', approved: 'Approved',
  in_progress: 'In progress', ready: 'Ready', closed: 'Released',
};

export default function PublicTrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/public/tracking/${token}`);
        setData(d);
      } catch {
        setError(true);
      }
    })();
  }, [token]);

  if (error) {
    return (
      <Shell>
        <div className="text-center space-y-2 py-10">
          <h1 className="text-xl font-semibold font-display">Tracking page not found</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">This link may have expired. Please contact your shop for an update.</p>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <Skeleton className="h-16 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl mt-4" />
      </Shell>
    );
  }

  if (data.state === 'inactive') {
    return (
      <Shell>
        <div className="text-center space-y-3 py-10">
          <h1 className="text-xl font-semibold font-display">{data.shop?.name}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            This tracking page is no longer active. For an update on your vehicle, please contact the shop directly.
          </p>
          {data.shop?.mobile && (
            <a href={`tel:${data.shop.mobile}`} className="inline-flex items-center gap-2 font-medium text-[hsl(var(--primary))]">
              <PhoneCall className="h-4 w-4" /> {data.shop.mobile}
            </a>
          )}
        </div>
      </Shell>
    );
  }

  const ui = STATUS_UI[data.status] || STATUS_UI.received;
  const Icon = ui.icon;

  return (
    <Shell>
      {/* Shop header — this page carries the shop's brand */}
      <div className="flex items-center gap-3 pb-4 border-b border-black/5">
        {data.shop.logo_url ? (
          <img src={fileUrl(data.shop.logo_url)} alt="" className="h-11 w-11 rounded-xl object-cover" />
        ) : (
          <div className="h-11 w-11 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center font-semibold">
            {data.shop.name?.slice(0, 1)}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-semibold truncate">{data.shop.name}</p>
          {data.shop.mobile && (
            <a href={`tel:${data.shop.mobile}`} className="text-xs text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1">
              <PhoneCall className="h-3 w-3" /> {data.shop.mobile}
            </a>
          )}
        </div>
      </div>

      {/* Vehicle */}
      <div className="mt-4 flex items-center justify-between gap-2">
        {data.vehicle?.plate && <PlateChip plate={data.vehicle.plate} />}
        <span className={`text-xs font-semibold rounded-full px-3 py-1 ${ui.pill}`} data-testid="tracking-status-pill">
          {PILL_LABEL[data.status] || data.status}
        </span>
      </div>
      {data.vehicle && (
        <p className="mt-2 font-semibold">
          {[data.vehicle.year, data.vehicle.make, data.vehicle.model].filter(Boolean).join(' ')}
          {data.vehicle.color ? ` · ${data.vehicle.color}` : ''}
        </p>
      )}

      {/* Current status hero */}
      <div className={`mt-4 rounded-2xl p-4 ${ui.bg}`} data-testid="tracking-status-hero">
        <p className={`font-semibold flex items-center gap-2 ${ui.fg}`}>
          <Icon className="h-5 w-5" /> {data.status_title}
        </p>
        <p className={`mt-1.5 text-sm ${ui.fg}`}>{data.status_body}</p>
      </div>

      {/* Timeline — newest first */}
      <p className="mt-6 mb-2 text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Updates</p>
      <div className="space-y-1">
        {data.events.map((e, i) => (
          <div key={i} className="flex gap-2.5 py-1">
            <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-none ${i === 0 ? 'text-emerald-600' : 'text-slate-300'}`} />
            <div>
              <p className={`text-sm ${i === 0 ? 'font-medium' : 'text-[hsl(var(--muted-foreground))]'}`}>{e.label}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] font-mono">{manilaDateTime(e.at)}</p>
            </div>
          </div>
        ))}
        {data.events.length === 0 && (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Updates will appear here as work moves along.</p>
        )}
      </div>

      <p className="mt-8 text-center text-xs text-[hsl(var(--muted-foreground))]">
        This page updates automatically — refresh anytime. Questions? Call the shop above.
      </p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-dvh bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(14,165,164,0.10),transparent_55%),radial-gradient(900px_circle_at_90%_10%,rgba(253,231,179,0.30),transparent_50%)] px-4 py-8">
      <div className="max-w-md mx-auto bg-white rounded-3xl shadow-[var(--shadow-card)] border border-black/5 p-5">
        {children}
      </div>
    </div>
  );
}
